import React, { createContext, useContext, useId } from "react"
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  FormProvider,
  useFormContext,
  useFormState,
  UseFormStateProps,
} from "react-hook-form"
import { useTranslation } from "react-i18next"
import { WarningCircle } from "@phosphor-icons/react"
import * as LabelPrimitive from "@radix-ui/react-label"
import { Slot } from "@radix-ui/react-slot"
import { Label } from "@/components/ui/label"
import { Tag } from "@/components/ui/tag"
import { cn } from "@/lib/utils"

export const Form = FormProvider

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName
}

const FormFieldContext = createContext<FormFieldContextValue>({} as FormFieldContextValue)

export const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  )
}

export const useFormField = () => {
  const fieldContext = useContext(FormFieldContext)
  const itemContext = useContext(FormItemContext)
  const { getFieldState, formState } = useFormContext()

  const fieldState = getFieldState(fieldContext.name, formState)

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>")
  }

  const { id } = itemContext

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  }
}

type FormItemContextValue = { id: string }

const FormItemContext = createContext<FormItemContextValue>({} as FormItemContextValue)

export const FormItem = ({
  className,
  ref,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> }) => {
  const id = useId()

  return (
    <FormItemContext.Provider value={{ id }}>
      <div ref={ref} className={cn("space-y-2", className)} {...props} />
    </FormItemContext.Provider>
  )
}

export const FormLabel = ({ className, ...props }: React.ComponentPropsWithRef<typeof LabelPrimitive.Root>) => {
  const { error, formItemId } = useFormField()

  return <Label className={cn(error && "text-destructive", className)} htmlFor={formItemId} {...props} />
}

export const FormControl = ({ ...props }: React.ComponentPropsWithRef<typeof Slot>) => {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField()

  return (
    <Slot
      id={formItemId}
      aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
      aria-invalid={!!error}
      {...props}
    />
  )
}

type FormParagraphProps = React.HTMLAttributes<HTMLParagraphElement> & { ref?: React.Ref<HTMLParagraphElement> }
export const FormDescription = ({ className, ...props }: FormParagraphProps) => {
  const { formDescriptionId } = useFormField()

  return <p id={formDescriptionId} className={cn("text-muted-foreground text-sm", className)} {...props} />
}

export const FormMessage = ({ className, children, ...props }: FormParagraphProps) => {
  const { error, formMessageId } = useFormField()
  const body = error ? String(error?.message) : children

  if (!body) {
    return null
  }

  return (
    <p id={formMessageId} className={cn("text-destructive text-sm font-medium", className)} {...props}>
      {body}
    </p>
  )
}

export const FormError = ({ children, className, ...props }: FormParagraphProps) => {
  return (
    <p
      data-testid="error-message"
      data-validation-error=""
      className={cn("text-critical relative mt-0.75 ml-4 pl-5 text-start text-xs tracking-wide", className)}
      {...props}>
      <span className="absolute top-px left-0 leading-none">
        <WarningCircle className="h-4 w-4" />
      </span>
      {children}
    </p>
  )
}

export const FormStatus = <TFieldValue extends FieldValues>({
  formControl,
}: {
  formControl: UseFormStateProps<TFieldValue>
}) => {
  const { isDirty, isSubmitted } = useFormState(formControl)
  const { t } = useTranslation()

  return (
    <>
      {isDirty && <Tag label={t("common.tag.unsavedChanges")} />}
      {!isDirty && isSubmitted && <Tag type="success" label={t("common.tag.saved")} />}
    </>
  )
}
